import React, { useEffect, useState } from 'react';
import { Card, PageHeader, Button, Badge, Input } from '../../components/shared/ui';
import { extendedAdminAPI } from '../../utils/api';
import useAuthStore from '../../store/authStore';

const AdminAdmins = () => {
  const { user } = useAuthStore();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    extendedAdminAPI.getAdmins().then(r => setAdmins(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3500); };

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await extendedAdminAPI.addAdmin(form);
      setForm({ email: '', password: '' });
      setShowAdd(false);
      load();
      flash('Admin eklendi.');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    try {
      await extendedAdminAPI.deleteAdmin(id);
      setDeleteConfirm(null);
      load();
      flash('Admin silindi.');
    } catch (err) {
      flash(err.response?.data?.error || 'Silinemedi');
    }
  };

  const handleToggle = async (id) => {
    try {
      await extendedAdminAPI.toggleAdminActive(id);
      load();
    } catch (err) {
      flash(err.response?.data?.error || 'Güncellenemedi');
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

  return (
    <>
      <PageHeader title="Admin Yönetimi" subtitle="Admin hesaplarını ekle, pasif yap veya sil" />

      {message && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">{message}</div>
      )}

      <div className="mb-4 flex justify-end">
        <Button onClick={() => { setShowAdd(true); setFormError(''); }}>+ Yeni Admin Ekle</Button>
      </div>

      {/* Ekleme Modalı */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="font-semibold text-lg text-ink-900 mb-4">Yeni Admin Ekle</h3>
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">E-posta</label>
                <Input
                  type="email"
                  placeholder="admin@sirket.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Şifre</label>
                <Input
                  type="password"
                  placeholder="En az 8 karakter"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                />
              </div>
              {formError && <p className="text-red-600 text-sm">{formError}</p>}
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1">Ekle</Button>
                <Button variant="secondary" type="button" onClick={() => { setShowAdd(false); setForm({ email: '', password: '' }); }} className="flex-1">
                  İptal
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Silme Onayı */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6 text-center">
            <p className="text-ink-800 mb-1 font-medium">Admin silinecek:</p>
            <p className="text-ink-600 text-sm mb-4">{deleteConfirm.email}</p>
            <p className="text-red-600 text-sm mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Evet, Sil
              </button>
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} className="flex-1">
                İptal
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Admin Listesi */}
      <Card>
        {loading ? (
          <div className="text-center py-12 text-ink-400">Yükleniyor...</div>
        ) : admins.length === 0 ? (
          <div className="text-center py-12 text-ink-400">Admin bulunamadı</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-ink-500">
                  <th className="py-3 px-4 font-medium">E-posta</th>
                  <th className="py-3 px-4 font-medium">Kayıt Tarihi</th>
                  <th className="py-3 px-4 font-medium">Durum</th>
                  <th className="py-3 px-4 font-medium text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {admins.map(a => {
                  const isMe = a.id === user?.id;
                  return (
                    <tr key={a.id} className="hover:bg-ink-50/50">
                      <td className="py-3 px-4 font-medium text-ink-900">
                        {a.email}
                        {isMe && <span className="ml-2 text-xs text-brand-600 font-normal">(siz)</span>}
                      </td>
                      <td className="py-3 px-4 text-ink-500">{fmt(a.created_at)}</td>
                      <td className="py-3 px-4">
                        <Badge variant={a.is_active ? 'success' : 'error'}>
                          {a.is_active ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 justify-end">
                          {!isMe && (
                            <>
                              <button
                                onClick={() => handleToggle(a.id)}
                                className="text-xs px-3 py-1.5 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-100 transition-colors"
                              >
                                {a.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(a)}
                                className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Sil
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
};

export default AdminAdmins;
